/**
 * Display formatting for the billing card. The API hands us raw cents + ISO
 * datetimes (snake_case pass-through, see AdminBillingElement.types.ts), so the
 * card formats at render time rather than expecting pre-formatted strings.
 *
 * Locale is left as the runtime default — the host (orchid-v2) renders per
 * publication language, and threading a locale prop through the element can
 * come later if a publication ever needs one that differs from the runtime.
 */

import type { AdminBillingTranslate } from '../AdminBillingElement.types';

/** Cents → localized currency string. Round-dollar amounts drop the trailing
 *  ".00" ($20), while amounts carrying cents keep both decimals ($20.25). */
export function formatCurrency(amountCents: number, currency: string): string {
  const hasFractionalCents = amountCents % 100 !== 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: hasFractionalCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

/** ISO 8601 datetime → "Jun 1, 2025". */
export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

/**
 * Curried translator with empty-string fallback. Lets each subcomponent treat
 * `translate` as required without re-deriving the same `?? ''` pattern.
 */
export function makeTranslator(translate: AdminBillingTranslate | undefined) {
  return (key: string, vars?: Record<string, string>) => translate?.(key, vars) ?? '';
}
